import { Box } from '@/components/atoms/Grid'
import { FC, memo, useState } from 'react'
import { Page, Sdk, SizeChart, SizeChartTable } from '@/network/graphql.g'
import { Tiny2 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'

import styled, { css } from 'styled-components'
import useGraphQl from 'hooks/useGraphQl'
import { InformationPage } from '@/types/informationPage'
import RulerIcon from '@/components/atoms/svg/RulerIcon'
import { useMhub } from '@/providers/mhubProvider'
import {
  ExperimentType,
  parseRunningUserExperiments,
  useExperiment
} from '@/providers/experimentProvider'
import {
  Desktop,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import dynamic from 'next/dynamic'
import { useCookie } from 'next-cookie'
import { Cookies } from '@/core/graphQlCookieManager'
import Spinner, { SpinnerSize } from '@/components/molecules/spinner'

const ProductDetailSizeTableMainTabletMobile = dynamic(
  () =>
    import(
      '@/components/organisms/productDetailSideMenu/productDetailSizeTableMainTabletMobile'
    ),
  {
    ssr: false
  }
)

const ProductDetailSizeTableMainDesktop = dynamic(
  () =>
    import(
      '@/components/organisms/productDetailSideMenu/productDetailSizeTableMainDesktop'
    ),
  {
    ssr: false
  }
)

const ProductDetailSizeTableGeneral = dynamic(
  () =>
    import(
      '@/components/organisms/productDetailSideMenu/productDetailSizeTableGeneral'
    ),
  {
    ssr: false
  }
)

type ProductDetailSizeTableProps = {
  productId: string
  productBrand: string
  name: string
  supplierId: number
  category: string
  iconPosition?: 'left' | 'right'
}

const ProductDetailSizeTable: FC<ProductDetailSizeTableProps> = ({
  productId,
  productBrand,
  name,
  supplierId,
  category,
  iconPosition = 'left'
}) => {
  const [sizeTableVisibility, setSizeTableVisibility] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mainSizeChart, setMainSizeChart] = useState<SizeChartTable>(null)
  const [sizeCharts, setSizeCharts] = useState<SizeChart[]>(null)
  const { t } = useTranslation('common')
  const { requestNotifyError } = useGraphQl()
  const { pushProductSizeTablePositionEvent } = useMhub()

  const { runningUserExperiments, getRunningExperiments } = useExperiment()
  const cookie = useCookie()

  const fakeTestUserExperiment = parseRunningUserExperiments(
    runningUserExperiments
  )
    .map(({ id, variant }) => ({
      experiment: getRunningExperiments().find((item) => item.id === id),
      variant
    }))
    .filter(
      (experiment) =>
        experiment?.experiment?.name === ExperimentType.FakeTestUser
    )
    .map(({ experiment: { id }, variant }) => `${id}.${variant}`)
    .join('!')

  const getActiveExperimentCookie = cookie.get(
    Cookies.activeExperimentsUserAffectionCookieName
  )
  const connectDedicatedExpToCookie = () => {
    const activeExperimentVariants = (getActiveExperimentCookie as string) || ''
    const experimentsArray = activeExperimentVariants.split('!')
    if (!experimentsArray.includes(fakeTestUserExperiment)) {
      const updatedExperimentVariants = activeExperimentVariants
        ? `${activeExperimentVariants}!${fakeTestUserExperiment}`
        : fakeTestUserExperiment
      cookie.set(
        Cookies.activeExperimentsUserAffectionCookieName,
        updatedExperimentVariants,
        {
          path: '/',
          expires: new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
        }
      )
    }
  }

  const getMainSizeTable = async (): Promise<SizeChartTable> => {
    const { success, response } = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.getMainSizeChart,
      variables: {
        productId
      }
    }))

    if (success && response?.getSizeChart) {
      const result = response?.getSizeChart
      setMainSizeChart(result)
      return result
    }
    return null
  }

  const getGeneralSizeTable = async (): Promise<SizeChart[]> => {
    if (sizeCharts) {
      return sizeCharts
    } else {
      const { success, response } = await requestNotifyError((sdk: Sdk) => ({
        method: sdk.getProductSizeChart,
        variables: {
          productId
        }
      }))
      if (success && response?.product?.sizeChart?.length > 0) {
        const result = response?.product?.sizeChart
        setSizeCharts(result)
        return result
      } else {
        const { success, response } = await requestNotifyError((sdk: Sdk) => ({
          method: sdk.getClientInformation,
          variables: {
            id: InformationPage.Sizes,
            page: Page.InformationPages
          },
          mainResponse: (response) => response?.information
        }))
        if (success) {
          const result = [
            { table: response?.information?.description?.description }
          ]
          setSizeCharts(result)
          return result
        }
      }
      return null
    }
  }

  const hideSizeTable = () => setSizeTableVisibility(false)

  const handleSizeTableClick = () => {
    setSizeTableVisibility(true)
    setIsLoading(true)
    setTimeout(async () => {
      connectDedicatedExpToCookie()
      const data = await getMainSizeTable()
      if (!data) {
        await getGeneralSizeTable()
      }
      setIsLoading(false)
      pushProductSizeTablePositionEvent(
        {
          id: productId,
          productBrand,
          name
        },
        supplierId,
        category
      )
    }, 0)
  }

  return (
    <Box>
      {sizeTableVisibility && (
        <MediaContextProvider>
          {isLoading ? (
            <StyledSpinner />
          ) : (
            <>
              {mainSizeChart ? (
                <>
                  <Desktop>
                    <ProductDetailSizeTableMainDesktop
                      mainSizeChart={mainSizeChart}
                      hideSizeTable={hideSizeTable}
                    />
                  </Desktop>
                  <TabletOrMobile>
                    <ProductDetailSizeTableMainTabletMobile
                      mainSizeChart={mainSizeChart}
                      hideSizeTable={hideSizeTable}
                    />
                  </TabletOrMobile>
                </>
              ) : (
                <ProductDetailSizeTableGeneral
                  sizeCharts={sizeCharts}
                  hideSizeTable={hideSizeTable}
                />
              )}
            </>
          )}
        </MediaContextProvider>
      )}
      <SizesTableText
        onClick={handleSizeTableClick}
        opacity={0.6}
        iconPosition={iconPosition}
        data-testid="sizeTable"
      >
        <RulerIcon width={30} height={20} />
        {t('ProductDetail.Sizes.table')}
      </SizesTableText>
    </Box>
  )
}

export default memo(ProductDetailSizeTable)

const SizesTableText = styled(Tiny2)`
  display: flex;
  align-items: center;
  opacity: 0.6;
  margin: 0;
  &:hover {
    cursor: pointer;
  }
  ${({ iconPosition }) =>
    iconPosition === 'right'
      ? css`
          flex-direction: row-reverse;
          & svg {
            margin-left: 10px;
          }
        `
      : css`
          & svg {
            margin-right: 10px;
          }
        `}
`

const position = (size) => `calc(50% - ${size / 2}px)`

const StyledSpinner = styled(Spinner)`
  position: fixed;
  top: ${position(SpinnerSize)};
  left: ${position(SpinnerSize)};
  z-index: 1000;
`
